import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { toast } from 'react-toastify';
import { Leaf, Check, ChevronDown, ChevronUp, Loader } from 'lucide-react';
import { api } from '../services/api';

/**
 * Lawn plans and mowing add-ons on a mowing bid — on the Bids page and on the
 * bidder's on-site sheet.
 *
 * Owner, Sep 18 2026: tap a plan and see "the key info, simple to read": apps
 * a year, the price per app next to the local going rate, what's in it, and
 * the offers (the standing one: mowing customers get these prices — we are
 * already there). Add-ons each choose every mow or one-time.
 *
 * Two modes: with a quoteId the picks go straight onto the bid; with
 * value/onChange the picks ride the on-site form until it is saved.
 */
export type Cadence = string;   // 'mow:1' | 'mow:2' | 'mow:3' | 'month:1' | 'month:2' | 'month:3' | 'month:4' | 'month:6' | 'once'
export type BidPicks = { plan: string | null; offer: string | null; addons: { key: string; cadence: Cadence }[] };
export const emptyPicks: BidPicks = { plan: null, offer: null, addons: [] };

const money = (n: any) => `$${Number(n || 0).toFixed(0)}`;

export default function BidPlanPicker({ quoteId, propertyId, sqft, kind, canEdit = true, value, onChange, onRefresh, compact = false }: {
  quoteId?: number | null; propertyId?: number | null; sqft?: string | number | null; kind?: string | null;
  canEdit?: boolean; value?: BidPicks; onChange?: (v: BidPicks) => void; onRefresh?: () => void; compact?: boolean;
}) {
  const deferred = !quoteId;
  const url = quoteId ? `/quotes/${quoteId}/plan-options` : `/quotes/plan-options`;
  // `kind` is what the sheet is bidding: a bid that sells no mow is offered no mowing add-ons, so the server sends
  // none back (services/bidPlans addonsRide). A saved bid carries its own kind, so it is only sent before one exists.
  const params = quoteId ? {} : { ...(propertyId ? { property_id: propertyId } : {}), ...(Number(sqft) > 0 ? { sqft } : {}), ...(kind ? { kind } : {}) };
  const { data, isLoading, refetch } = useQuery(['plan-options', quoteId || null, propertyId || null, String(sqft || ''), kind || ''],
    () => api.get(url, { params }).then(r => r.data), { staleTime: 60_000 });
  const [open, setOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [picks, setPicks] = useState<BidPicks>(value || emptyPicks);
  // In immediate mode the bid is the truth; in deferred mode the form is.
  useEffect(() => {
    if (!deferred && data?.on_bid) setPicks({ plan: data.on_bid.plan, offer: data.on_bid.offer, addons: data.on_bid.addons.map((a: any) => ({ key: a.key, cadence: a.cadence })) });
  }, [data, deferred]);
  useEffect(() => { if (deferred && value) setPicks(value); }, [value, deferred]);
  // The bid stopped carrying add-ons (its kind changed to a project): whatever was ticked goes with them, so a pick
  // made under the old kind never rides.
  useEffect(() => {
    if (!deferred || !data || data.offers_addons !== false || !picks.addons.length) return;
    commit({ ...picks, addons: [] });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.offers_addons, deferred, picks.addons.length]);
  const commit = (next: BidPicks) => { setPicks(next); if (deferred) onChange?.(next); };

  async function putPlan(plan: string | null, offer: string | null) {
    if (deferred) { commit({ ...picks, plan, offer }); return; }
    setBusy(true);
    try {
      const r = plan ? await api.post(`/quotes/${quoteId}/plan`, { plan, offer_key: offer || null }) : await api.delete(`/quotes/${quoteId}/plan`);
      toast.success(r.data?.message || 'Saved'); refetch(); onRefresh?.();
    } catch (e: any) { toast.error(e?.response?.data?.message || 'Could not change the plan'); }
    finally { setBusy(false); }
  }
  async function putAddons(addons: BidPicks['addons']) {
    if (deferred) { commit({ ...picks, addons }); return; }
    setBusy(true);
    try { const r = await api.put(`/quotes/${quoteId}/addons`, { items: addons }); toast.success(r.data?.message || 'Saved'); refetch(); onRefresh?.(); }
    catch (e: any) { toast.error(e?.response?.data?.message || 'Could not save the add-ons'); }
    finally { setBusy(false); }
  }

  if (isLoading) return <div className="py-4 flex justify-center"><Loader className="h-4 w-4 animate-spin text-brand-600" /></div>;
  if (!data) return null;
  const pad = compact ? 'p-3' : 'p-4';

  return (
    <div className="space-y-3">
      {/* ── Lawn plans ── */}
      <div>
        <div className="flex items-baseline justify-between">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Lawn plans for this yard</p>
          <p className="text-[11px] text-gray-500">
            {data.sqft_known ? `${Number(data.sqft).toLocaleString()} sq ft` : 'typical 6,000 sq ft — measure to price it'}{data.grass_name ? ` · ${data.grass_name}` : ''}{data.mow ? '' : ' · not a mow yard, half again'}
          </p>
        </div>
        <div className="mt-1.5 space-y-1.5">
          {data.plans.map((p: any) => {
            const on = picks.plan === p.plan;
            const showing = open === p.plan;
            return (
              <div key={p.plan} className={`rounded-xl border ${on ? 'border-brand-500 bg-brand-50' : p.recommended ? 'border-gray-300 bg-white' : 'border-gray-200 bg-white'}`}>
                <button type="button" onClick={() => setOpen(showing ? null : p.plan)} className={`w-full text-left ${pad} flex items-center gap-3`}>
                  <Leaf className={`h-4 w-4 flex-shrink-0 ${on ? 'text-brand-700' : 'text-emerald-600'}`} />
                  <span className="flex-1 min-w-0">
                    <span className="font-semibold text-gray-900">{p.name}</span>
                    <span className="text-xs text-gray-500"> · {p.kicker}</span>
                    <span className="block text-xs text-gray-600">{p.visits} apps a year · {money(p.per_app)} an app{data.local?.per_app?.range ? ` · local ${data.local.per_app.range}` : ''}</span>
                  </span>
                  <span className="text-right">
                    <span className="block font-bold text-gray-900">{money(p.monthly)}<span className="text-xs font-normal text-gray-500">/mo</span></span>
                    {on && <span className="text-[10px] font-semibold text-brand-700">on the bid</span>}
                  </span>
                  {showing ? <ChevronUp className="h-4 w-4 text-gray-500" /> : <ChevronDown className="h-4 w-4 text-gray-500" />}
                </button>
                {showing && (
                  <div className={`${pad} pt-0 space-y-2.5 text-sm`}>
                    {/* The key info, simple to read */}
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="rounded-lg bg-white border border-gray-200 py-2"><p className="text-lg font-bold text-gray-900">{p.visits}</p><p className="text-[11px] text-gray-500">apps a year</p></div>
                      <div className="rounded-lg bg-white border border-gray-200 py-2"><p className="text-lg font-bold text-gray-900">{money(p.per_app)}</p><p className="text-[11px] text-gray-500">per app</p></div>
                      <div className="rounded-lg bg-white border border-gray-200 py-2"><p className="text-lg font-bold text-gray-900">{data.local?.per_app?.range || '—'}</p><p className="text-[11px] text-gray-500">local, per app{data.local?.per_app?.sweet ? ` · most ${data.local.per_app.sweet}` : ''}</p></div>
                    </div>
                    {/* His own market table (Settings → Plan offers): the year beside the year. Never on the customer's page. */}
                    {data.local?.per_year?.range && (
                      <p className="text-xs text-gray-600">Local yearly programs run <b>{data.local.per_year.range}</b>{data.local.per_year.sweet ? <> (most {data.local.per_year.sweet})</> : null} · this plan is <b>{money(p.yearly)}</b> a year.</p>
                    )}
                    <p className="text-gray-700">{p.tag}</p>
                    <ul className="text-gray-700 space-y-0.5">
                      {p.includes.map((t: string) => <li key={t} className="flex items-start gap-1.5"><Check className="h-3.5 w-3.5 mt-0.5 text-emerald-600 flex-shrink-0" />{t}</li>)}
                      {p.not_included.map((t: string) => <li key={t} className="flex items-start gap-1.5 text-gray-500"><span className="w-3.5 text-center">–</span>{t}</li>)}
                    </ul>
                    <p className="text-xs text-gray-500">{p.items.map((i: any) => `${i.count} ${i.word}`).join(' · ')} — {money(p.yearly)} a year + tax</p>
                    {p.months?.length > 0 && (
                      <div>
                        <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">The year — each one rides a mow that month</p>
                        <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs text-gray-700">
                          {p.months.map((m: any) => <p key={m.month}><span className="font-medium text-gray-900">{String(m.month).slice(0, 3)}</span> — {m.words}</p>)}
                        </div>
                      </div>
                    )}
                    {p.offers?.length > 0 && (
                      <div className="space-y-1">
                        <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Offers</p>
                        {p.offers.map((o: any) => (
                          <label key={o.key} className={`flex items-start gap-2 rounded-lg border px-2.5 py-2 ${picks.offer === o.key ? 'border-brand-400 bg-white' : 'border-gray-200 bg-white'}`}>
                            {o.line ? (
                              <input type="radio" name={`offer-${p.plan}`} checked={picks.offer === o.key} onChange={() => commit({ ...picks, offer: o.key })} disabled={!canEdit} className="mt-1" />
                            ) : <Check className="h-3.5 w-3.5 mt-1 text-emerald-600" />}
                            <span className="text-xs"><span className="font-semibold text-gray-900">{o.title}</span>{o.line ? <span className="text-brand-700"> — {money(-o.line.unit_price)} off</span> : null}<span className="block text-gray-600">{o.blurb}</span></span>
                          </label>
                        ))}
                        {picks.offer && p.offers.some((o: any) => o.key === picks.offer && o.line) && (
                          <button type="button" onClick={() => commit({ ...picks, offer: null })} className="text-[11px] text-gray-500 underline">no offer</button>
                        )}
                      </div>
                    )}
                    <p className="text-xs text-gray-500">{data.how_a_visit_works}</p>
                    {canEdit && (
                      on ? (
                        <button type="button" onClick={() => putPlan(null, null)} disabled={busy} className="w-full py-2.5 rounded-xl border border-gray-300 text-sm font-semibold text-gray-700 disabled:opacity-50">Take it off the bid</button>
                      ) : (
                        <button type="button" onClick={() => putPlan(p.plan, picks.offer)} disabled={busy} className="press w-full py-2.5 rounded-xl bg-brand-600 text-black text-sm font-semibold disabled:opacity-50">
                          {busy ? '…' : `Put ${p.name} on the bid — ${money(p.monthly)}/mo`}
                        </button>
                      )
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {!deferred && picks.plan && <p className="mt-1 text-[11px] text-gray-500">Billed monthly at month end, separate from the mow. Starts the moment they accept.</p>}
      </div>

      {/* ── Mowing add-ons ── */}
      {data.addons?.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Mowing add-ons</p>
          <div className="mt-1.5 rounded-xl border border-gray-200 bg-white divide-y divide-gray-100">
            {data.addons.map((a: any) => {
              const cur = picks.addons.find(x => x.key === a.key);
              const defaultCadence: Cadence = a.key === 'bed_spray' ? 'month:1' : a.key === 'bush_trim' ? 'month:4' : 'once';
              return (
                <div key={a.key} className="px-3 py-2 flex items-center gap-2">
                  <input type="checkbox" checked={!!cur} disabled={!canEdit || busy}
                    onChange={e => putAddons(e.target.checked ? [...picks.addons, { key: a.key, cadence: defaultCadence }] : picks.addons.filter(x => x.key !== a.key))}
                    className="rounded text-brand-600" />
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm text-gray-900">{a.name} <span className="text-gray-500">· {money(a.price)}</span></span>
                    <span className="block text-[11px] text-gray-500 truncate">{a.blurb}{a.sheet_priced ? ` · ${a.priced_from}` : ''}</span>
                  </span>
                  {cur && (
                    <select value={cur.cadence} disabled={!canEdit || busy} onChange={e => putAddons(picks.addons.map(x => x.key === a.key ? { ...x, cadence: e.target.value } : x))}
                      className="border border-gray-300 rounded-lg px-2 py-1 text-xs bg-white text-gray-800">
                      {(data.cadences || []).map((c: any) => <option key={c.key} value={c.key}>{c.label}</option>)}
                    </select>
                  )}
                </div>
              );
            })}
          </div>
          <p className="mt-1 text-[11px] text-gray-500">Cadences follow the mowing: "every other mow" counts mows; "once a month" and "every N months" land on the first mow of that month (biweekly → every 2nd mow, weekly → every 4th); one-time goes on the first visit. Prices are for this yard, per application.</p>
        </div>
      )}
    </div>
  );
}
